
import { supabase } from '@/integrations/supabase/client';
import type { Comment } from '@/types/social';
import type { CommentPostParams, CommentServiceResult, CardCommentInsert, PollCommentInsert } from './types';
import { getTableConfig } from './utils';

/**
 * Add a new comment
 */
export const postComment = async ({
  contentId,
  content,
  userId,
  anonymousId,
  parentId = null,
  contentType
}: CommentPostParams): Promise<CommentServiceResult> => {
  try {
    const { tableName, idField } = getTableConfig(contentType);
    
    // Only use one identification method - userId takes precedence
    const baseCommentData = {
      content,
      parent_id: parentId,
      user_id: userId || null,
      anonymous_id: userId ? null : anonymousId // Only use anonymousId if userId doesn't exist
    };
    
    console.log('Creating comment with data:', { 
      contentId,
      contentType,
      hasUserId: !!userId,
      hasAnonymousId: !!anonymousId,
      usingAnonymousId: !userId && !!anonymousId,
      parentId
    });
    
    let data;
    let error;
    
    if (tableName === 'card_comments') {
      // Add card_id to the comment data for card comments
      const cardCommentData: CardCommentInsert = {
        ...baseCommentData,
        card_id: contentId
      };
      
      const result = await supabase
        .from('card_comments')
        .insert(cardCommentData)
        .select('*')
        .single();
        
      data = result.data;
      error = result.error;
    } else {
      // For poll comments, use direct insertion
      const pollCommentData: PollCommentInsert = {
        ...baseCommentData,
        poll_id: contentId
      };
      
      const result = await supabase
        .from('poll_comments')
        .insert(pollCommentData)
        .select('*')
        .single();
        
      data = result.data;
      error = result.error;
    }
    
    if (error) {
      return { success: false, error };
    }
    
    if (!data) {
      return { 
        success: false, 
        error: new Error("Comment was inserted but no data was returned") 
      };
    }
    
    // Add profile data manually instead of trying to join
    if (data.user_id) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id, name')
        .eq('id', data.user_id)
        .single();
        
      if (profileData) {
        data.profile = {
          id: profileData.id,
          name: profileData.name || 'User',
          avatar_url: null
        };
      } else {
        data.profile = {
          id: data.user_id,
          name: 'User',
          avatar_url: null
        };
      }
    } else {
      data.profile = {
        id: null,
        name: 'Anonymous User',
        avatar_url: null
      };
    }
    
    return { success: true, data: data as Comment };
  } catch (error) {
    console.error(`Error posting comment to ${contentType}:`, error);
    return { 
      success: false, 
      error: error instanceof Error ? error : new Error(`Unknown error posting ${contentType} comment`) 
    };
  }
};
